// JournalFeedback.tsx - AI 對用戶日記的回饋
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  generateDayCloseReflectionFeedback,
  generateFallbackFeedback,
  DayCloseReflectionFeedbackResponse,
  DayCloseReflectionFeedbackRequest
} from '../../../services/api/dayCloseReflectionFeedback';
import { useDayCloseAPI } from '../../../hooks/useDayCloseAPI';
import { logger } from '../../../utils/logger';

interface JournalFeedbackProps {
  journalContent: string;
  dayCloseId: string;
  userId: string;
  onComplete: () => void;
  onSkip: () => void;
}

const JournalFeedback: React.FC<JournalFeedbackProps> = ({
  journalContent,
  dayCloseId,
  userId,
  onComplete,
  onSkip
}) => {
  // Helper function to render text with paragraph breaks
  const renderParagraphs = (text: string) => {
    if (!text) return null;

    // Split by double line breaks and filter out empty paragraphs
    const paragraphs = text.split('\n\n').filter(paragraph => paragraph.trim().length > 0);

    // If only one paragraph, render as single p
    if (paragraphs.length === 1) {
      return <p className="text-base">{paragraphs[0].trim()}</p>;
    }

    // Multiple paragraphs, render each separately
    return (
      <>
        {paragraphs.map((paragraph, index) => (
          <p key={index} className="text-base">
            {paragraph.trim()}
          </p>
        ))}
      </>
    );
  };
  const { loadDayClose, loadTodayTimeBoxes } = useDayCloseAPI();
  const [feedbackResult, setFeedbackResult] = useState<DayCloseReflectionFeedbackResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Generate feedback
  const generateFeedback = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Create new AbortController
      abortControllerRef.current = new AbortController();

      // Set 30 second timeout
      const timeoutId = setTimeout(() => {
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
      }, 30000);

      // Get Day Close data
      const dayCloseData = await loadDayClose(dayCloseId);
      if (!dayCloseData) {
        throw new Error('Day close data not found');
      }

      // Get today's time boxes data
      const todayTimeBoxes = await loadTodayTimeBoxes(userId);

      // Prepare dayContext data
      const dayContext: DayCloseReflectionFeedbackRequest['dayContext'] = {
        completed: dayCloseData.completedTimeBoxes || [],
        incomplete: dayCloseData.incompleteActions
          ?.filter(action => !action.itemType || action.itemType === 'timeBox') // 只取 TimeBox 相關的
          ?.map(action => ({
            timeBoxId: action.timeBoxId || action.itemId,
            action: action.action
          })) || [],
        timeBoxes: todayTimeBoxes.map(tb => ({
          id: tb.id,
          title: tb.title,
          category: tb.category as string,
          startTime: tb.startTime.toISOString(),
          endTime: tb.endTime.toISOString(),
          status: tb.status || 'normal'
        })),
        captures: []
      };

      // Get today's date
      const today = new Date();
      const dateString = today.getFullYear() + '-' +
        String(today.getMonth() + 1).padStart(2, '0') + '-' +
        String(today.getDate()).padStart(2, '0');

      // Call AI feedback API
      const feedback = await generateDayCloseReflectionFeedback({
        reflectionContent: journalContent,
        dayContext,
        date: dateString
      }, abortControllerRef.current.signal);

      // Clear timeout
      clearTimeout(timeoutId);

      // Check if already cancelled
      if (abortControllerRef.current?.signal.aborted) {
        return;
      }

      setFeedbackResult(feedback);

    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        logger.info('JournalFeedback', 'Feedback generation cancelled');
        return;
      }

      logger.error('JournalFeedback', 'Generate feedback failed', error);

      // Use fallback feedback
      const fallbackFeedback = generateFallbackFeedback(journalContent);
      setFeedbackResult(fallbackFeedback);
      setError('Unable to generate personalized insights, showing general feedback');

    } finally {
      setIsLoading(false);
    }
  }, [journalContent, dayCloseId, userId, loadDayClose, loadTodayTimeBoxes]);

  // Auto-generate feedback on component mount
  useEffect(() => {
    generateFeedback();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [generateFeedback]);

  // Retry feedback generation
  const handleRetry = useCallback(() => {
    generateFeedback();
  }, [generateFeedback]);

  // Cancel and skip
  const handleCancel = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    onSkip();
  }, [onSkip]);

  return (
    <div className="p-6">
      {/* Loading state */}
      {isLoading && (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="w-8 h-8 border-2 border-sky-500 border-t-transparent rounded-full animate-spin mb-4"></div>
          <p className="text-gray-600 text-sm mb-4">Reflecting on your day...</p>
          <button
            onClick={handleCancel}
            className="text-gray-500 hover:text-gray-700 text-sm underline"
          >
            Skip
          </button>
        </div>
      )}

      {/* Error state */}
      {error && !isLoading && (
        <div className="py-4 text-center mb-6">
          <p className="text-amber-600 text-sm mb-3">{error}</p>
          <button
            onClick={handleRetry}
            className="text-sky-600 hover:text-sky-700 text-sm underline mr-4"
          >
            Try again
          </button>
          <button
            onClick={onSkip}
            className="text-gray-500 hover:text-gray-700 text-sm underline"
          >
            Skip
          </button>
        </div>
      )}

      {/* Feedback result - Natural text flow */}
      {feedbackResult && !isLoading && (
        <div className="prose prose-gray max-w-none mb-8">
          <div className="space-y-4 text-gray-700 leading-relaxed">
            {/* Emotional support section */}
            {renderParagraphs(feedbackResult.emotionalSupport)}

            {/* Learning capture section (if exists) */}
            {feedbackResult.learningCapture && renderParagraphs(feedbackResult.learningCapture)}

            {/* Inspirational content section (if exists) */}
            {feedbackResult.inspirationalContent && (
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 mt-4">
                <div className="text-gray-700 leading-relaxed">
                  {renderParagraphs(feedbackResult.inspirationalContent.text)}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default JournalFeedback;
